export const WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY = "webapp_signup_before_demo_v1";

export const WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS = {
  control: "control",
  signupFirst: "signup_before_demo",
} as const;

export const WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY = "webapp_signup_before_demo";
export const WEBAPP_SIGNUP_BEFORE_DEMO_READY_QUERY_KEY = "webapp_demo";
export const WEBAPP_SIGNUP_BEFORE_DEMO_EXPOSURE_STORAGE_KEY =
  "chickytutor:experiment:webapp-signup-before-demo:v1";

export type WebappSignupBeforeDemoVariant =
  (typeof WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS)[keyof typeof WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS];

export type WebappSignupBeforeDemoExperimentProperties = {
  experiment_key: typeof WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY;
  experiment_variant: WebappSignupBeforeDemoVariant;
  experiment_assignment_id?: string;
};

export type WebappSignupBeforeDemoAttributionProperties = {
  webapp_signup_before_demo_experiment_key: typeof WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY;
  webapp_signup_before_demo_variant: WebappSignupBeforeDemoVariant;
  webapp_signup_before_demo_assignment_id?: string;
};

export type WebappSignupBeforeDemoExposure = {
  properties: Required<WebappSignupBeforeDemoExperimentProperties>;
  newlyExposed: boolean;
};

const EXPOSURE_TTL_MS = 8 * 24 * 60 * 60 * 1000;

type StoredExposure = Required<WebappSignupBeforeDemoExperimentProperties> & {
  exposed_at_ms: number;
};

export function parseWebappSignupBeforeDemoVariant(
  value: unknown
): WebappSignupBeforeDemoVariant | null {
  return value === WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.control ||
    value === WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.signupFirst
    ? value
    : null;
}

function createAssignmentId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `webapp_signup_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readStoredExposure(storage: Storage, nowMs: number): StoredExposure | null {
  try {
    const parsed = JSON.parse(
      storage.getItem(WEBAPP_SIGNUP_BEFORE_DEMO_EXPOSURE_STORAGE_KEY) || "null"
    ) as Partial<StoredExposure> | null;
    const variant = parseWebappSignupBeforeDemoVariant(parsed?.experiment_variant);
    if (
      !parsed ||
      parsed.experiment_key !== WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY ||
      !variant ||
      typeof parsed.experiment_assignment_id !== "string" ||
      !parsed.experiment_assignment_id.trim() ||
      typeof parsed.exposed_at_ms !== "number" ||
      nowMs < parsed.exposed_at_ms ||
      nowMs - parsed.exposed_at_ms > EXPOSURE_TTL_MS
    ) {
      return null;
    }

    return {
      experiment_key: WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY,
      experiment_variant: variant,
      experiment_assignment_id: parsed.experiment_assignment_id,
      exposed_at_ms: parsed.exposed_at_ms,
    };
  } catch {
    return null;
  }
}

export function recordWebappSignupBeforeDemoExposure({
  variant,
  storage = typeof window === "undefined" ? null : window.localStorage,
  nowMs = Date.now(),
}: {
  variant: WebappSignupBeforeDemoVariant;
  storage?: Storage | null;
  nowMs?: number;
}): WebappSignupBeforeDemoExposure {
  const existing = storage ? readStoredExposure(storage, nowMs) : null;
  if (existing?.experiment_variant === variant) {
    const { exposed_at_ms: _exposedAtMs, ...properties } = existing;
    return { properties, newlyExposed: false };
  }

  const properties: Required<WebappSignupBeforeDemoExperimentProperties> = {
    experiment_key: WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY,
    experiment_variant: variant,
    experiment_assignment_id: createAssignmentId(),
  };

  if (storage) {
    try {
      storage.setItem(
        WEBAPP_SIGNUP_BEFORE_DEMO_EXPOSURE_STORAGE_KEY,
        JSON.stringify({ ...properties, exposed_at_ms: nowMs } satisfies StoredExposure)
      );
    } catch {
      // Experiment attribution must never block the speaking flow.
    }
  }

  return { properties, newlyExposed: true };
}

export function getWebappSignupBeforeDemoExperimentProperties({
  storage = typeof window === "undefined" ? null : window.localStorage,
  search = typeof window === "undefined" ? "" : window.location.search,
  nowMs = Date.now(),
}: {
  storage?: Storage | null;
  search?: string;
  nowMs?: number;
} = {}): WebappSignupBeforeDemoExperimentProperties | Record<string, never> {
  if (storage) {
    const exposure = readStoredExposure(storage, nowMs);
    if (exposure) {
      const { exposed_at_ms: _exposedAtMs, ...properties } = exposure;
      return properties;
    }
  }

  const returnVariant = parseWebappSignupBeforeDemoVariant(
    new URLSearchParams(search).get(WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY)
  );
  return returnVariant
    ? {
        experiment_key: WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY,
        experiment_variant: returnVariant,
      }
    : {};
}

export function getWebappSignupBeforeDemoAttributionProperties(
  options: Parameters<typeof getWebappSignupBeforeDemoExperimentProperties>[0] = {}
): WebappSignupBeforeDemoAttributionProperties | Record<string, never> {
  const properties = getWebappSignupBeforeDemoExperimentProperties(options);
  const variant = parseWebappSignupBeforeDemoVariant(properties.experiment_variant);
  if (!variant) return {};

  return {
    webapp_signup_before_demo_experiment_key: WEBAPP_SIGNUP_BEFORE_DEMO_EXPERIMENT_KEY,
    webapp_signup_before_demo_variant: variant,
    ...(properties.experiment_assignment_id
      ? { webapp_signup_before_demo_assignment_id: properties.experiment_assignment_id }
      : {}),
  };
}

export function restoreWebappSignupBeforeDemoExposureFromSearch({
  search,
  storage = typeof window === "undefined" ? null : window.localStorage,
  nowMs = Date.now(),
}: {
  search: string;
  storage?: Storage | null;
  nowMs?: number;
}): WebappSignupBeforeDemoExposure | null {
  const variant = parseWebappSignupBeforeDemoVariant(
    new URLSearchParams(search).get(WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY)
  );
  return variant ? recordWebappSignupBeforeDemoExposure({ variant, storage, nowMs }) : null;
}

export function isWebappSignupBeforeDemoReturnPath(value: string | null | undefined): boolean {
  if (!value?.startsWith("/")) return false;
  try {
    const url = new URL(value, "https://www.chickytutor.com");
    return (
      url.origin === "https://www.chickytutor.com" &&
      url.pathname === "/" &&
      url.searchParams.get(WEBAPP_SIGNUP_BEFORE_DEMO_READY_QUERY_KEY) === "ready" &&
      parseWebappSignupBeforeDemoVariant(
        url.searchParams.get(WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY)
      ) === WEBAPP_SIGNUP_BEFORE_DEMO_VARIANTS.signupFirst
    );
  } catch {
    return false;
  }
}

export function buildWebappSignupBeforeDemoAuthUrl({
  currentPath = "/",
  currentSearch = "",
  variant,
}: {
  currentPath?: string;
  currentSearch?: string;
  variant: WebappSignupBeforeDemoVariant;
}): string {
  const safePath = currentPath === "/" ? currentPath : "/";
  const returnParams = new URLSearchParams(currentSearch);
  returnParams.delete("oauth_signup");
  returnParams.set(WEBAPP_SIGNUP_BEFORE_DEMO_READY_QUERY_KEY, "ready");
  returnParams.set(WEBAPP_SIGNUP_BEFORE_DEMO_QUERY_KEY, variant);
  const redirect = `${safePath}?${returnParams.toString()}`;
  return `/auth?${new URLSearchParams({ redirect, intent: "free_demo" }).toString()}`;
}
